import dayjs from "dayjs";
import { QUESTION_TYPE, RATING_SCALE, RATING_SCALE_META } from "pages/SurveyManagement/constants";

export const CHANNEL_LABEL_KEYS = {
  link_qr: "feedbackCapture.channel_link_qr",
  web_embed: "feedbackCapture.channel_web_embed",
  triggered_send: "feedbackCapture.channel_triggered_send",
  in_app_sdk: "feedbackCapture.channel_in_app_sdk",
  phone_call: "feedbackCapture.channel_phone_call",
};

/** Label key for where a response came from; a kiosk response (link opened with its kiosk key) says Kiosk. */
export function channelLabelKey(response) {
  if (response?.kiosk) return "feedbackCapture.channel_kiosk";
  return CHANNEL_LABEL_KEYS[response?.channel_type] || "feedbackCapture.response_channel";
}

// A response written from an AI phone call (or its browser test) carries the call's conversation.
export function isCallResponse(response) {
  return String(response?.response_id || "").startsWith("elevenlabs:") || !!response?.meta?.conversation_id;
}

/** Seconds into the call as "m:ss" (01:05 style), or "" when unknown. */
export function formatClock(seconds) {
  const total = Number(seconds);
  if (seconds == null || !Number.isFinite(total) || total < 0) return "";
  const rounded = Math.floor(total);
  const minutes = Math.floor(rounded / 60);
  return `${String(minutes).padStart(2, "0")}:${String(rounded % 60).padStart(2, "0")}`;
}

function speaker(role, t) {
  return role === "agent"
    ? t("feedbackCapture.responsesPage.call_agent")
    : t("feedbackCapture.responsesPage.call_customer");
}

/** The whole call, one "[00:08] Customer: ..." line per turn. */
export function transcriptText(conversation, t) {
  return (conversation?.turns || [])
    .map((turn) => {
      const clock = formatClock(turn.start);
      return `${clock ? `[${clock}] ` : ""}${speaker(turn.role, t)}: ${turn.text}`;
    })
    .join("\n");
}

export const FAST_COMPLETION_SECONDS = 5;

// Columns the table shows next to name and email: choice questions first, then open text.
export const MAX_ANSWER_COLUMNS = 3;

export function formatAnswerValue(value) {
  if (value == null) return "";
  if (typeof value === "object" && !Array.isArray(value)) {
    // A "Country and city" answer; the API usually sends it formatted as display_value already.
    return [value.city, value.country].map((part) => String(part || "").trim()).filter(Boolean).join(", ");
  }
  if (Array.isArray(value)) {
    return value.filter((v) => v != null && String(v).trim()).join(", ");
  }
  return String(value).trim();
}

export function formatDuration(seconds, t) {
  const total = Number(seconds);
  if (seconds == null || !Number.isFinite(total) || total < 0) return null;
  const rounded = Math.round(total);
  if (rounded < 60) {
    return t("feedbackCapture.response_duration_seconds", { count: rounded });
  }
  const minutes = Math.floor(rounded / 60);
  const rem = rounded % 60;
  if (rem === 0) {
    return t("feedbackCapture.response_duration_minutes", { count: minutes });
  }
  return t("feedbackCapture.response_duration_minutes_seconds", {
    minutes,
    seconds: rem,
  });
}

export function durationSeconds(response) {
  const value = response?.completion_time_seconds ?? response?.meta?.completion_time_seconds;
  const total = Number(value);
  return value == null || !Number.isFinite(total) || total < 0 ? null : total;
}

function formatPagePath(pageUrl) {
  if (!pageUrl) return "";
  try {
    const parsed = new URL(pageUrl);
    return `${parsed.pathname}${parsed.search}` || parsed.hostname;
  } catch (_) {
    return pageUrl;
  }
}

function formatReferrer(referrerUrl) {
  if (!referrerUrl) return null;
  try {
    return new URL(referrerUrl).hostname.replace(/^www\./, "");
  } catch (_) {
    return referrerUrl;
  }
}

/** Technical details shown under "More info": IP, device, page, campaign, company. */
export function responseMetaLines(response, t) {
  const meta = response.meta;
  if (!meta || typeof meta !== "object") return [];
  const lines = [];
  if (meta.ip) {
    lines.push(`${t("feedbackCapture.response_meta_ip")}: ${meta.ip}`);
  }
  if (meta.user_agent) {
    lines.push(`${t("feedbackCapture.response_meta_device")}: ${meta.user_agent}`);
  }
  const clientDevice = meta.client_device;
  if (clientDevice && typeof clientDevice === "object") {
    const parts = [
      clientDevice.platform,
      clientDevice.screen,
      clientDevice.language,
      clientDevice.timezone,
    ].filter(Boolean);
    if (parts.length) {
      lines.push(`${t("feedbackCapture.response_meta_client")}: ${parts.join(" · ")}`);
    }
  }
  const pagePath = formatPagePath(response.page_url || meta.page_url);
  if (pagePath) {
    lines.push(`${t("feedbackCapture.response_meta_page")}: ${pagePath}`);
  }
  const referrer = formatReferrer(response.referrer_url ?? meta.referrer_url);
  if (referrer) {
    lines.push(`${t("feedbackCapture.response_meta_referrer")}: ${referrer}`);
  }
  const utm = response.utm_params || meta.utm_params;
  if (utm && typeof utm === "object") {
    const campaign = utm.utm_campaign || utm.utm_source;
    if (campaign) {
      lines.push(`${t("feedbackCapture.response_meta_campaign")}: ${campaign}`);
    }
  }
  if (response.company_domain || meta.company_domain) {
    lines.push(
      `${t("feedbackCapture.response_meta_company")}: ${
        response.company_domain || meta.company_domain
      }`
    );
  }
  const triggerWait =
    response.time_on_page_before_trigger_seconds ?? meta.time_on_page_before_trigger_seconds;
  if (triggerWait != null && Number(triggerWait) >= 0) {
    lines.push(
      `${t("feedbackCapture.response_meta_trigger_wait")}: ${formatDuration(triggerWait, t)}`
    );
  }
  return lines;
}

/** "Consent given · 26 Oct 2026, 14:05" when the respondent ticked the survey's consent box. */
export function consentSummary(response, t, format = "D MMM YYYY, HH:mm", withVersion = true) {
  const consent = response?.consent;
  if (!consent || consent.given !== true) return null;
  const at = consent.at ? dayjs(consent.at) : null;
  const label = t("feedbackCapture.responsesPage.consent_given");
  const parts = [label];
  if (at && at.isValid()) parts.push(at.format(format));
  // Consent before starting numbers the owner's wording; say which one was agreed to.
  if (withVersion && consent.mode === "start" && consent.version) {
    parts.push(t("feedbackCapture.responsesPage.consent_version", { version: consent.version }));
  }
  return parts.join(" · ");
}

/** The consent text version a response agreed to, for the Excel column ("" when none). */
export function consentVersion(response) {
  const consent = response?.consent;
  if (!consent || consent.given !== true || consent.version == null) return "";
  return String(consent.version);
}

export function respondentName(response) {
  const contact = response?.contact || {};
  return [contact.first_name, contact.last_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
}

export function respondentEmail(response) {
  return String(response?.contact?.email || "").trim();
}

export function respondentInitials(response) {
  const name = respondentName(response) || respondentEmail(response);
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

/** Number shown as "#N": the oldest response is #1 (responses arrive newest first). */
export function responseNumber(response, allResponses) {
  const index = allResponses.findIndex((item) => item.id === response.id);
  return index >= 0 ? allResponses.length - index : 0;
}

export function answerText(response, questionId) {
  const answer = (response?.answers || []).find((a) => a.question_id === questionId);
  if (!answer) return "";
  return answer.display_value || formatAnswerValue(answer.value);
}

const CHOICE_TYPES = [QUESTION_TYPE.SINGLE_CHOICE, QUESTION_TYPE.MULTIPLE_CHOICE, QUESTION_TYPE.RATING];
// Summarised as a list of answers rather than option counts.
const TEXT_SUMMARY_TYPES = [
  QUESTION_TYPE.OPEN_TEXT,
  QUESTION_TYPE.PHONE,
  QUESTION_TYPE.LOCATION,
  QUESTION_TYPE.EMAIL,
  QUESTION_TYPE.URL,
  QUESTION_TYPE.VIDEO,
];

export function pickColumnQuestions(questions, max = MAX_ANSWER_COLUMNS) {
  const list = (questions || []).filter((q) => q && q.id);
  const choice = list.filter((q) => CHOICE_TYPES.includes(q.type));
  const rest = list.filter((q) => !CHOICE_TYPES.includes(q.type));
  return [...choice, ...rest].slice(0, max);
}

// Accent- and case-insensitive, so "izmir" finds "İzmir" and "sahin" finds "Şahin".
export function searchKey(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/ı/g, "i");
}

export function filterResponses(responses, { view = "completed", company = "all", search = "" } = {}) {
  const term = searchKey(String(search || "").trim());
  return (responses || []).filter((response) => {
    if (view === "completed" && response.status !== "completed") return false;
    if (view === "empty" && response.status === "completed") return false;
    if (company !== "all" && response.company_domain !== company) return false;
    if (!term) return true;
    const haystack = [
      respondentName(response),
      respondentEmail(response),
      response.text,
      ...(response.answers || []).map((a) => a.display_value || formatAnswerValue(a.value)),
    ]
      .filter(Boolean)
      .join(" \n ");
    return searchKey(haystack).includes(term);
  });
}

export function companyOptions(responses) {
  const counts = {};
  (responses || []).forEach((response) => {
    if (response.status !== "completed" || !response.company_domain) return;
    counts[response.company_domain] = (counts[response.company_domain] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([domain, count]) => ({ domain, count }));
}

export function responseCounts(responses) {
  const list = responses || [];
  const completed = list.filter((r) => r.status === "completed");
  const durations = completed.map(durationSeconds).filter((d) => d != null);
  return {
    total: list.length,
    completed: completed.length,
    incomplete: list.length - completed.length,
    completionRate: list.length ? Math.round((completed.length / list.length) * 100) : null,
    averageSeconds: durations.length
      ? Math.round(durations.reduce((sum, d) => sum + d, 0) / durations.length)
      : null,
    latestAt: list.reduce((latest, r) => {
      if (!r.completed_at) return latest;
      return !latest || dayjs(r.completed_at).isAfter(latest) ? r.completed_at : latest;
    }, null),
  };
}

function ratingBuckets(question) {
  const scale = question.ratingScale || question.scale || RATING_SCALE.ONE_TO_FIVE;
  const meta = RATING_SCALE_META[scale] || RATING_SCALE_META[RATING_SCALE.ONE_TO_FIVE];
  const values = [];
  for (let v = meta.max; v >= meta.min; v -= 1) values.push(String(v));
  return values;
}

/**
 * Per-question summary of completed responses: option counts for choice and rating questions,
 * every non-empty answer (newest first) for open text.
 */
export function buildQuestionSummaries(questions, responses) {
  const completed = (responses || []).filter((r) => r.status === "completed");
  return (questions || [])
    .filter((q) => q && q.id)
    .map((question) => {
      const answers = completed
        .map((response) => {
          const answer = (response.answers || []).find((a) => a.question_id === question.id);
          return answer ? { response, answer } : null;
        })
        .filter(Boolean);

      if (TEXT_SUMMARY_TYPES.includes(question.type)) {
        const texts = answers
          .map(({ response, answer }) => ({
            response,
            text: answer.display_value || formatAnswerValue(answer.value),
          }))
          .filter((item) => item.text);
        return { question, kind: "text", answered: texts.length, texts };
      }

      const labels =
        question.type === QUESTION_TYPE.RATING
          ? ratingBuckets(question)
          : (question.options || []).map((o) => String(o));
      // A Map keeps the scale order; object keys like "5" would be sorted ascending.
      const counts = new Map(labels.map((label) => [label, 0]));
      let answered = 0;
      let ratingSum = 0;
      answers.forEach(({ answer }) => {
        const values = Array.isArray(answer.value) ? answer.value : [answer.value];
        const picked = values.filter((v) => v != null && String(v).trim() !== "");
        if (!picked.length) return;
        answered += 1;
        picked.forEach((v) => {
          const key = String(v);
          counts.set(key, (counts.get(key) || 0) + 1);
          if (question.type === QUESTION_TYPE.RATING && Number.isFinite(Number(v))) {
            ratingSum += Number(v);
          }
        });
      });
      const options = [...counts.entries()].map(([label, count]) => ({
        label,
        count,
        percent: answered ? Math.round((count / answered) * 100) : 0,
      }));
      return {
        question,
        kind: "choice",
        answered,
        options,
        average:
          question.type === QUESTION_TYPE.RATING && answered
            ? Math.round((ratingSum / answered) * 10) / 10
            : null,
      };
    });
}

/**
 * Header row and data rows for the Excel export, one row per response. ``conversations`` (response
 * id → call conversation) adds the phone calls' transcripts as a last column.
 */
/** The video of a video answer: {url, duration_seconds, transcript, deleted_at}, or null. */
export function answerVideo(response, questionId) {
  const answer = (response?.answers || []).find((a) => a.question_id === questionId);
  return answer?.video || null;
}

// A video question takes three columns in the Excel file: its length, the video link and the transcript.
function exportColumns(question, t) {
  const title = question.text || question.id;
  if (question.type !== QUESTION_TYPE.VIDEO) return [title];
  return [
    title,
    `${title} · ${t("feedbackCapture.responsesPage.export_video_link")}`,
    `${title} · ${t("feedbackCapture.responsesPage.export_video_transcript")}`,
  ];
}

function exportCells(question, response, t) {
  const text = answerText(response, question.id);
  if (question.type !== QUESTION_TYPE.VIDEO) return [text];
  const video = answerVideo(response, question.id);
  if (!video) return [text, "", ""];
  return [
    text,
    video.deleted_at ? t("feedbackCapture.responsesPage.video_deleted_short") : video.url || "",
    video.transcript || "",
  ];
}

export function buildExportTable(questions, responses, allResponses, t, conversations = {}) {
  const list = (questions || []).filter((q) => q && q.id);
  const answered = new Set();
  (responses || []).forEach((r) => (r.answers || []).forEach((a) => answered.add(a.question_id)));
  // Answers to questions no longer in the survey still belong in the file.
  const extraIds = [...answered].filter((id) => !list.some((q) => q.id === id));
  const hasText = (responses || []).some((r) => r.text);
  const hasConsent = (responses || []).some((r) => r.consent);
  const hasCalls = (responses || []).some((r) => conversations?.[r.id]);

  const headers = [
    t("feedbackCapture.responsesPage.export_number"),
    t("feedbackCapture.responsesPage.export_submitted"),
    t("feedbackCapture.responsesPage.export_status"),
    t("feedbackCapture.responsesPage.export_first_name"),
    t("feedbackCapture.responsesPage.export_last_name"),
    t("feedbackCapture.responsesPage.export_email"),
    ...list.flatMap((q) => exportColumns(q, t)),
    ...extraIds,
    ...(hasText ? [t("feedbackCapture.response_text_capture")] : []),
    t("feedbackCapture.responsesPage.export_duration_seconds"),
    t("feedbackCapture.responsesPage.export_channel"),
    t("feedbackCapture.responsesPage.export_company"),
    ...(hasConsent
      ? [t("feedbackCapture.responsesPage.export_consent"), t("feedbackCapture.responsesPage.export_consent_version")]
      : []),
    ...(hasCalls ? [t("feedbackCapture.responsesPage.export_transcript")] : []),
  ];

  const rows = (responses || []).map((response) => {
    const contact = response.contact || {};
    const duration = durationSeconds(response);
    return [
      responseNumber(response, allResponses || responses),
      response.completed_at ? dayjs(response.completed_at).format("YYYY-MM-DD HH:mm") : "",
      response.status === "completed"
        ? t("feedbackCapture.responses_filter_completed")
        : t("feedbackCapture.response_status_empty"),
      contact.first_name || "",
      contact.last_name || "",
      contact.email || "",
      ...list.flatMap((q) => exportCells(q, response, t)),
      ...extraIds.map((id) => answerText(response, id)),
      ...(hasText ? [response.text || ""] : []),
      duration == null ? "" : Math.round(duration),
      response.channel_type ? t(channelLabelKey(response)) : "",
      response.company_domain || "",
      ...(hasConsent
        ? [consentSummary(response, t, "YYYY-MM-DD HH:mm", false) || "", consentVersion(response)]
        : []),
      ...(hasCalls ? [transcriptText(conversations[response.id], t)] : []),
    ];
  });
  return { headers, rows };
}

function fileSlug(title) {
  return (
    String(title || "survey")
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/ı/g, "i")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "survey"
  );
}

export function exportFileName(title, date = dayjs()) {
  return `${fileSlug(title)}-responses-${date.format("YYYY-MM-DD")}.xlsx`;
}

/** "voyage-torba-response-1-transcript.txt" / "...-recording.mp3" for one call's downloads. */
export function callFileName(title, number, kind) {
  return kind === "audio"
    ? `${fileSlug(title)}-response-${number}-recording.mp3`
    : `${fileSlug(title)}-response-${number}-transcript.txt`;
}

/**
 * The downloadable transcript of one call: the survey and response, each question with the
 * agent's words, the customer's own words and the recorded answer, then the whole conversation.
 */
export function conversationAsText(response, conversation, { surveyTitle, number } = {}, t) {
  const lines = [];
  if (surveyTitle) lines.push(surveyTitle);
  const heading = [t("feedbackCapture.responsesPage.response_number", { number })];
  if (response?.completed_at) heading.push(dayjs(response.completed_at).format("D MMM YYYY, HH:mm"));
  const duration = formatDuration(conversation?.duration_seconds ?? durationSeconds(response), t);
  if (duration) heading.push(t("feedbackCapture.responsesPage.call_duration", { duration }));
  lines.push(heading.join(" · "), "");

  const pairs = conversation?.pairs || [];
  if (pairs.length) {
    lines.push(`== ${t("feedbackCapture.responsesPage.call_tab_pairs")} ==`, "");
    pairs.forEach((pair, index) => {
      lines.push(`${index + 1}. ${pair.question_text || pair.question_id}`);
      if (pair.asked) {
        lines.push(`${speaker("agent", t)}: ${pair.asked}`);
        lines.push(`${speaker("customer", t)}: ${pair.answer || t("feedbackCapture.responsesPage.call_no_answer")}`);
      } else {
        lines.push(t("feedbackCapture.responsesPage.call_not_asked"));
      }
      const recorded = answerText(response, pair.question_id);
      if (recorded) lines.push(`${t("feedbackCapture.responsesPage.call_recorded_answer")}: ${recorded}`);
      lines.push("");
    });
  }

  lines.push(`== ${t("feedbackCapture.responsesPage.call_tab_transcript")} ==`, "", transcriptText(conversation, t));
  return `${lines.join("\n").trim()}\n`;
}

/** Plain-text copy of one response for the clipboard. */
export function responseAsText(response, questions, t) {
  const lines = [];
  const name = respondentName(response);
  const email = respondentEmail(response);
  if (name) lines.push(name);
  if (email) lines.push(email);
  if (lines.length) lines.push("");
  const order = (questions || []).map((q) => q.id);
  const answers = [...(response.answers || [])].sort((a, b) => {
    const ia = order.indexOf(a.question_id);
    const ib = order.indexOf(b.question_id);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });
  answers.forEach((answer) => {
    lines.push(answer.question_text || answer.question_id);
    lines.push(answer.display_value || formatAnswerValue(answer.value) || "—");
    lines.push("");
  });
  if (response.text) {
    lines.push(t("feedbackCapture.response_text_capture"));
    lines.push(response.text);
  }
  return lines.join("\n").trim();
}
